

import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Check,
  Lock,
  AlertCircle,
  Search,
  Filter,
  ChevronDown,
  Coins,
  Gem,
  X,
  User,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalFooter,
} from '@/components/ui/modal';
import { PlayerCardCompact } from '@/components/cards/PlayerCard';
import {
  Card,
  CardRarity,
  RarityGate,
  RARITY_CONFIG,
} from '@/types';
import { nflConfig } from '@/config/sports';
import { GateMeter, gatesMet, type BestBallGates } from './GateMeter';

// ============================================================================
// TYPES
// ============================================================================

interface RosterSlot {
  slot: string;
  position?: string;
  positions?: string[];
  required: boolean;
}

interface RosterRequirements {
  rosterSlots: RosterSlot[];
}

// Rarity gate validation map
const RARITY_GATE_REQUIREMENTS: Record<RarityGate, CardRarity[]> = {
  OPEN: ['PROSPECT', 'ROOKIE', 'VETERAN', 'ALL_STAR', 'GOAT'],
  RARE_PLUS: ['ROOKIE', 'VETERAN', 'ALL_STAR', 'GOAT'],
  ELITE_PLUS: ['VETERAN', 'ALL_STAR', 'GOAT'],
  LEGENDARY_PLUS: ['ALL_STAR', 'GOAT'],
  PRIME_ONLY: ['GOAT'],
};

// Default roster slots if none provided (NBA)
const DEFAULT_ROSTER_SLOTS: RosterSlot[] = [
  { slot: 'PG', position: 'PG', required: true },
  { slot: 'SG', position: 'SG', required: true },
  { slot: 'SF', position: 'SF', required: true },
  { slot: 'PF', position: 'PF', required: true },
  { slot: 'C', position: 'C', required: true },
  { slot: 'G', positions: ['PG', 'SG'], required: true },
  { slot: 'F', positions: ['SF', 'PF'], required: true },
  { slot: 'UTIL', positions: ['PG', 'SG', 'SF', 'PF', 'C'], required: true },
];

// NFL Best Ball slots — the 8 starters from the sport config plus 2 bench
// slots. The slot map is a UX device; the server derives canonical assignments
// from the 10 cardIds on submit.
const NFL_ALL_POSITIONS = nflConfig.positions.map((p) => p.id);
const NFL_ROSTER_SLOTS: RosterSlot[] = [
  ...nflConfig.defaultRosterSlots.map((s) =>
    s.eligiblePositions.length === 1
      ? { slot: s.slot, position: s.eligiblePositions[0], required: true }
      : { slot: s.slot, positions: [...s.eligiblePositions], required: true },
  ),
  { slot: 'BENCH1', positions: NFL_ALL_POSITIONS, required: true },
  { slot: 'BENCH2', positions: NFL_ALL_POSITIONS, required: true },
];

// Slot display config (NBA positions + NFL Best Ball slots)
const SLOT_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  PG: { label: 'PG', color: 'text-[#2563EB]', bgColor: 'bg-[#2563EB]/20' },
  SG: { label: 'SG', color: 'text-cyan-400', bgColor: 'bg-cyan-500/20' },
  SF: { label: 'SF', color: 'text-verdigris', bgColor: 'bg-verdigris/20' },
  PF: { label: 'PF', color: 'text-orange-400', bgColor: 'bg-orange-500/20' },
  C: { label: 'C', color: 'text-oxblood', bgColor: 'bg-oxblood/20' },
  G: { label: 'G', color: 'text-indigo-400', bgColor: 'bg-indigo-500/20' },
  F: { label: 'F', color: 'text-emerald-400', bgColor: 'bg-emerald-500/20' },
  UTIL: { label: 'UTIL', color: 'text-gold', bgColor: 'bg-gold/20' },
  // NFL
  QB: { label: 'QB', color: 'text-[#DC2626]', bgColor: 'bg-[#DC2626]/20' },
  RB1: { label: 'RB1', color: 'text-emerald-400', bgColor: 'bg-emerald-500/20' },
  RB2: { label: 'RB2', color: 'text-emerald-400', bgColor: 'bg-emerald-500/20' },
  WR1: { label: 'WR1', color: 'text-[#2563EB]', bgColor: 'bg-[#2563EB]/20' },
  WR2: { label: 'WR2', color: 'text-[#2563EB]', bgColor: 'bg-[#2563EB]/20' },
  TE: { label: 'TE', color: 'text-orange-400', bgColor: 'bg-orange-500/20' },
  K: { label: 'K', color: 'text-indigo-400', bgColor: 'bg-indigo-500/20' },
  FLEX: { label: 'FLEX', color: 'text-gold', bgColor: 'bg-gold/20' },
  BENCH1: { label: 'BN', color: 'text-warm-gray', bgColor: 'bg-zinc-500/20' },
  BENCH2: { label: 'BN', color: 'text-warm-gray', bgColor: 'bg-zinc-500/20' },
};

export interface RosterSlotAssignment {
  slot: string;
  cardId: string;
}

/** Active league lock in the /api/cards payload (two-slot model). */
interface LeagueLock {
  durationClass: 'SEASON' | 'MONTHLY';
  competitionId?: string;
  competitionName: string;
}

/** League locks off the API card payload (Card's domain type predates them). */
function cardLeagueLocks(card: Card): LeagueLock[] {
  const locks = (card as unknown as { locks?: LeagueLock[] }).locks;
  return Array.isArray(locks)
    ? locks.filter((l) => l.durationClass === 'SEASON' || l.durationClass === 'MONTHLY')
    : [];
}

/** Card-league context: cumulative gates + which lock slot this league occupies. */
export interface BestBallEntryContext {
  gates: BestBallGates;
  durationClass: 'SEASON' | 'MONTHLY' | null;
  /**
   * Copy branch only: BEST_BALL "your best 8 score automatically" vs DRAFT_PPR
   * "you set your starting 8 each week". Entry mechanics are identical.
   */
  format?: 'BEST_BALL' | 'DRAFT_PPR';
}

/**
 * Structured entry failure. The parent's onConfirm should throw one of these
 * (built from the API error body) so the modal can render code-specific
 * messaging (GATE_NOT_MET shortfalls, ROSTER_SHAPE_INVALID missing slots, …).
 */
export class BestBallEntryError extends Error {
  readonly body: { error?: string; code?: string } & Record<string, unknown>;

  constructor(body: { error?: string; code?: string } & Record<string, unknown>) {
    super(body.error || body.code || 'Entry failed');
    this.name = 'BestBallEntryError';
    this.body = body;
  }
}

const SLOT_ORDER_LABELS: Record<string, string> = {
  QB: 'a Quarterback',
  RB: 'a Running Back',
  WR: 'a Wide Receiver',
  TE: 'a Tight End',
  K: 'a Kicker',
  FLEX: 'a Flex (RB/WR/TE)',
};

/** Map an entry failure to user-facing copy (structured codes first). */
function describeEntryError(err: unknown, rosterSize: number): string {
  if (err instanceof BestBallEntryError) {
    const b = err.body;
    switch (b.code) {
      case 'GATE_NOT_MET': {
        const parts: string[] = [];
        if (typeof b.minRare === 'number') {
          parts.push(`${b.minRare} Rare or better (you have ${b.haveRare ?? 0})`);
        }
        if (typeof b.minLegendary === 'number' && (b.minLegendary as number) > 0) {
          parts.push(`${b.minLegendary} Legendary (you have ${b.haveLegendary ?? 0})`);
        }
        return `This league requires ${parts.join(' and ')}.`;
      }
      case 'ROSTER_SHAPE_INVALID': {
        const missing = Array.isArray(b.missing) ? (b.missing as string[]) : [];
        const named = missing.map((m) => SLOT_ORDER_LABELS[m] ?? m).join(', ');
        return missing.length > 0
          ? `Your 10 cards can't field a starting lineup — still needed: ${named}.`
          : 'Your 10 cards can’t field a full starting lineup.';
      }
      case 'CARD_LOCKED_SLOT_TAKEN':
        return `A selected card is already locked in ${b.leagueName ?? 'another league'} for this window.`;
      case 'ROSTER_SIZE_INVALID':
        return `Select exactly ${rosterSize} cards.`;
      case 'DUPLICATE_PLAYER':
        return 'One card per player — remove the duplicate.';
      case 'CARD_NOT_OWNED':
      case 'CARD_BURNED':
        return 'A selected card is no longer available — refresh and try again.';
      case 'BEST_BALL_JOIN_CLOSED':
        return 'Entry closed — the first game has kicked off.';
      case 'INSUFFICIENT_BALANCE':
        return 'Not enough coins for the entry fee.';
      default:
        return err.message;
    }
  }
  return err instanceof Error ? err.message : 'Failed to enter';
}

interface RosterSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (cardIds: string[], slotAssignments: RosterSlotAssignment[]) => void | Promise<void>;
  cards: Card[];
  rosterSize: number;
  rarityGate?: RarityGate;
  rosterSlots?: RosterSlot[];
  loading?: boolean;
  entryFee?: number;
  entryFeeType?: 'COINS' | 'GEMS' | 'FREE';
  userBalance?: number;
  userGems?: number;
  /** Sport adapter — 'NFL' swaps in the Best Ball slot layout. */
  sport?: string;
  /**
   * Best Ball league context. Turns the rarity floor OFF (gates are cumulative
   * counts, Commons allowed), renders the GateMeter, and disables only cards
   * locked in the SAME duration class ("in {league}" microcopy).
   */
  bestBall?: BestBallEntryContext | null;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function RosterSelectionModal({
  isOpen,
  onClose,
  onConfirm,
  cards,
  rosterSize = 8,
  rarityGate = 'OPEN',
  rosterSlots,
  loading = false,
  entryFee = 0,
  entryFeeType = 'FREE',
  userBalance = 0,
  userGems = 0,
  sport = 'NBA',
  bestBall = null,
}: RosterSelectionModalProps) {
  // Map of slot -> cardId
  const [slotAssignments, setSlotAssignments] = useState<Record<string, string>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [positionFilter, setPositionFilter] = useState<string>('ALL');
  const [activeSlot, setActiveSlot] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [entryError, setEntryError] = useState<string | null>(null);

  const isBestBall = !!bestBall;
  const effectiveSlots =
    rosterSlots ?? (sport === 'NFL' ? NFL_ROSTER_SLOTS : DEFAULT_ROSTER_SLOTS);

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSlotAssignments({});
      setSearchQuery('');
      setPositionFilter('ALL');
      setActiveSlot(null);
      setSubmitting(false);
      setEntryError(null);
    }
  }, [isOpen]);

  // Get allowed rarities for this gate. Best Ball turns the floor OFF —
  // its gates are cumulative counts (Commons allowed), metered separately.
  const allowedRarities = isBestBall
    ? RARITY_GATE_REQUIREMENTS.OPEN
    : RARITY_GATE_REQUIREMENTS[rarityGate];

  // Filter and categorize cards. Best Ball: cards locked in the SAME duration
  // class stay visible but blocked ("in {league}"); other-class locks stay
  // fully pickable (a monthly lock doesn't consume the season slot).
  const { eligibleCards, ineligibleCards, blockedReasons, positions } = useMemo(() => {
    const eligible: Card[] = [];
    const ineligible: Card[] = [];
    const reasons = new Map<string, string>();
    const posSet = new Set<string>();

    cards.forEach((card) => {
      // Check rarity gate
      const meetsRarityGate = allowedRarities.includes(card.rarity);

      if (card.player?.position) {
        posSet.add(card.player.position);
      }

      let isAvailable: boolean;
      if (isBestBall) {
        const locks = cardLeagueLocks(card);
        const sameClassLock = bestBall?.durationClass
          ? locks.find((l) => l.durationClass === bestBall.durationClass)
          : undefined;
        if (sameClassLock) {
          isAvailable = false;
          reasons.set(card.id, `in ${sameClassLock.competitionName}`);
        } else if (card.isLocked && locks.length === 0) {
          // Locked outside the league system (e.g. marketplace listing).
          isAvailable = false;
          reasons.set(card.id, 'unavailable');
        } else {
          isAvailable = true;
        }
      } else {
        isAvailable = !card.isLocked;
      }

      if (meetsRarityGate && isAvailable) {
        eligible.push(card);
      } else {
        ineligible.push(card);
      }
    });

    return {
      eligibleCards: eligible,
      ineligibleCards: ineligible,
      blockedReasons: reasons,
      positions: Array.from(posSet).sort(),
    };
  }, [cards, allowedRarities, isBestBall, bestBall]);

  // Get assigned card IDs
  const assignedCardIds = useMemo(() => {
    return new Set(Object.values(slotAssignments));
  }, [slotAssignments]);

  // Get assigned player IDs for uniqueness check
  const assignedPlayerIds = useMemo(() => {
    const playerIds = new Set<string>();
    Object.values(slotAssignments).forEach((cardId) => {
      const card = cards.find((c) => c.id === cardId);
      if (card?.playerId) {
        playerIds.add(card.playerId);
      }
    });
    return playerIds;
  }, [slotAssignments, cards]);

  // Get allowed positions for active slot
  const allowedPositionsForActiveSlot = useMemo(() => {
    if (!activeSlot) return [];
    const slot = effectiveSlots.find((s) => s.slot === activeSlot);
    if (!slot) return [];
    if (slot.position) return [slot.position];
    if (slot.positions) return slot.positions;
    return [];
  }, [activeSlot, effectiveSlots]);

  // Apply search and filters (and slot position filter)
  const filteredCards = useMemo(() => {
    return eligibleCards.filter((card) => {
      // Already assigned to another slot
      if (assignedCardIds.has(card.id)) return false;

      // Player already used in another slot
      if (card.playerId && assignedPlayerIds.has(card.playerId)) return false;

      // Active slot position filter
      if (activeSlot && allowedPositionsForActiveSlot.length > 0) {
        if (!card.player?.position || !allowedPositionsForActiveSlot.includes(card.player.position)) {
          return false;
        }
      }

      // Manual position filter
      if (positionFilter !== 'ALL' && card.player?.position !== positionFilter) {
        return false;
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = card.player?.name?.toLowerCase().includes(query);
        const matchesTeam = card.player?.team?.toLowerCase().includes(query);
        if (!matchesName && !matchesTeam) return false;
      }

      return true;
    });
  }, [eligibleCards, searchQuery, positionFilter, activeSlot, allowedPositionsForActiveSlot, assignedCardIds, assignedPlayerIds]);

  // Blocked cards shown (not hidden) with their reason — Best Ball only.
  const blockedCardsForSlot = useMemo(() => {
    if (!isBestBall || !activeSlot) return [];
    return ineligibleCards.filter((card) => {
      if (!blockedReasons.has(card.id)) return false;
      if (allowedPositionsForActiveSlot.length > 0) {
        if (!card.player?.position || !allowedPositionsForActiveSlot.includes(card.player.position)) {
          return false;
        }
      }
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = card.player?.name?.toLowerCase().includes(query);
        const matchesTeam = card.player?.team?.toLowerCase().includes(query);
        if (!matchesName && !matchesTeam) return false;
      }
      return true;
    });
  }, [isBestBall, activeSlot, ineligibleCards, blockedReasons, allowedPositionsForActiveSlot, searchQuery]);

  // Assign card to active slot
  const assignCardToSlot = (card: Card) => {
    if (!activeSlot) return;

    setSlotAssignments((prev) => ({
      ...prev,
      [activeSlot]: card.id,
    }));

    // Auto-advance to next empty slot
    const currentIndex = effectiveSlots.findIndex((s) => s.slot === activeSlot);
    const nextEmptySlot = effectiveSlots.find((s, i) => i > currentIndex && !slotAssignments[s.slot]);
    setActiveSlot(nextEmptySlot?.slot || null);
  };

  // Remove card from slot
  const removeFromSlot = (slotName: string) => {
    setSlotAssignments((prev) => {
      const newAssignments = { ...prev };
      delete newAssignments[slotName];
      return newAssignments;
    });
  };

  // Get card for a slot
  const getCardForSlot = (slotName: string): Card | undefined => {
    const cardId = slotAssignments[slotName];
    return cardId ? cards.find((c) => c.id === cardId) : undefined;
  };

  // Validation
  const filledSlots = Object.keys(slotAssignments).length;
  const requiredSlots = effectiveSlots.filter((s) => s.required).length;

  // Best Ball: cumulative class gates must clear before confirm unlocks.
  const selectedRarities = useMemo(
    () =>
      Object.values(slotAssignments)
        .map((cardId) => cards.find((c) => c.id === cardId)?.rarity)
        .filter((r): r is CardRarity => !!r),
    [slotAssignments, cards],
  );
  const gatesSatisfied = !isBestBall || !bestBall || gatesMet(bestBall.gates, selectedRarities);

  const isValid = filledSlots >= requiredSlots && gatesSatisfied;

  const canAfford = entryFeeType === 'FREE'
    || (entryFeeType === 'COINS' && userBalance >= entryFee)
    || (entryFeeType === 'GEMS' && userGems >= entryFee);

  // Final check before entry: cards lock to this event, so require an explicit
  // "yes, lock them" confirmation on top of the Enter button.
  const [confirmingLock, setConfirmingLock] = useState(false);
  useEffect(() => {
    if (!isOpen) setConfirmingLock(false);
  }, [isOpen]);

  const lockScopeCopy = isBestBall
    ? bestBall?.durationClass === 'MONTHLY'
      ? 'They will be unavailable to other monthly leagues (and marketplace listings) until this league ends.'
      : 'They will be unavailable to other season leagues (and marketplace listings) until the season ends.'
    : 'They will be unavailable to other events until this event ends.';

  const handleConfirm = async () => {
    if (isValid && !submitting && canAfford) {
      setSubmitting(true);
      setEntryError(null);
      try {
        const cardIds = Object.values(slotAssignments);
        const slotAssignmentsArray: RosterSlotAssignment[] = Object.entries(slotAssignments).map(
          ([slot, cardId]) => ({ slot, cardId })
        );
        await onConfirm(cardIds, slotAssignmentsArray);
      } catch (err) {
        setEntryError(describeEntryError(err, effectiveSlots.length));
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <Modal open={isOpen} onOpenChange={onClose}>
      <ModalContent className="relative max-w-5xl max-h-[90vh] flex flex-col">
        <ModalHeader>
          <ModalTitle>{isBestBall ? 'Build Your Lineup' : 'Build Your Roster'}</ModalTitle>
          <ModalDescription>
            {isBestBall
              ? `Fill 8 starting slots and 2 bench slots. Cards lock for the ${
                  bestBall?.durationClass === 'MONTHLY' ? 'month' : 'season'
                } — ${
                  bestBall?.format === 'DRAFT_PPR'
                    ? 'you set your starting 8 each week.'
                    : 'your best 8 score automatically each week.'
                }`
              : 'Fill each position slot with a card from your collection. Cards will be locked until the event ends.'}
          </ModalDescription>
        </ModalHeader>

        {/* Rarity gate info (floor gates — non-Best-Ball events) */}
        {!isBestBall && rarityGate !== 'OPEN' && (
          <div className="mx-4 p-3 bg-gold/10 border border-gold/30 rounded-lg">
            <div className="flex items-center gap-2 text-gold text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>
                Requires {rarityGate.replace('_', ' ').toLowerCase()} cards:{' '}
                {allowedRarities.map((r) => RARITY_CONFIG[r].label).join(', ')}
              </span>
            </div>
          </div>
        )}

        {/* Best Ball cumulative gates — live progress, Commons always allowed */}
        {isBestBall && bestBall && (
          <GateMeter
            gates={bestBall.gates}
            selectedRarities={selectedRarities}
            className="mx-4"
          />
        )}

        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left: Roster Slots */}
          <div className="w-80 flex flex-col border-r border-surface-3 bg-surface-2/50">
            <div className="p-4 border-b border-surface-3">
              <h3 className="font-semibold text-cream">
                Roster ({filledSlots}/{effectiveSlots.length})
              </h3>
              <div className="mt-2 h-2 bg-surface-3 rounded-full overflow-hidden">
                <div
                  className={cn(
                    'h-full transition-all duration-300',
                    filledSlots === effectiveSlots.length ? 'bg-verdigris' : 'bg-gold'
                  )}
                  style={{ width: `${(filledSlots / effectiveSlots.length) * 100}%` }}
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {effectiveSlots.map((slot) => {
                const card = getCardForSlot(slot.slot);
                const config = SLOT_CONFIG[slot.slot] || { label: slot.slot, color: 'text-warm-gray', bgColor: 'bg-zinc-500/20' };
                const isActive = activeSlot === slot.slot;

                return (
                  <div
                    key={slot.slot}
                    onClick={() => setActiveSlot(slot.slot)}
                    className={cn(
                      'p-3 rounded-lg border-2 cursor-pointer transition-all',
                      isActive
                        ? 'border-gold bg-gold/10'
                        : card
                        ? 'border-verdigris/50 bg-verdigris/5 hover:border-verdigris'
                        : 'border-surface-4 bg-surface-3/50 hover:border-zinc-600'
                    )}
                  >
                    <div className="flex items-center gap-3">
                      {/* Position badge */}
                      <div className={cn('px-2 py-1 rounded text-xs font-bold', config.bgColor, config.color)}>
                        {config.label}
                      </div>

                      {card ? (
                        // Filled slot
                        <div className="flex-1 min-w-0 flex items-center gap-2">
                          <div
                            className="w-1 h-8 rounded"
                            style={{ backgroundColor: RARITY_CONFIG[card.rarity]?.color || '#9CA3AF' }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-cream truncate">{card.player?.name}</p>
                            <p className="text-xs text-tertiary">{card.player?.team} - {RARITY_CONFIG[card.rarity]?.label}</p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFromSlot(slot.slot);
                            }}
                            className="p-1 text-tertiary hover:text-oxblood transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        // Empty slot
                        <div className="flex-1 flex items-center gap-2 text-tertiary">
                          <User className="w-4 h-4" />
                          <span className="text-sm">
                            {slot.positions ? slot.positions.join('/') : slot.position}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Entry fee summary */}
            {entryFee > 0 && entryFeeType !== 'FREE' && (
              <div className="p-4 border-t border-surface-3">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-warm-gray">Entry Fee</span>
                  <span className={cn('flex items-center gap-1', entryFeeType === 'GEMS' ? 'text-verdigris' : 'text-gold')}>
                    {entryFeeType === 'GEMS' ? <Gem className="w-4 h-4" /> : <Coins className="w-4 h-4" />}
                    {entryFee}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-warm-gray">Your Balance</span>
                  <span className={cn('font-medium', canAfford ? 'text-cream' : 'text-oxblood')}>
                    {entryFeeType === 'GEMS' ? userGems : userBalance} {entryFeeType.toLowerCase()}
                  </span>
                </div>
                {!canAfford && (
                  <p className="text-xs text-oxblood mt-2">Insufficient balance</p>
                )}
              </div>
            )}
          </div>

          {/* Right: Card browser */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Header */}
            <div className="px-4 py-3 border-b border-surface-3 bg-surface-2/30">
              <div className="flex items-center gap-3">
                {activeSlot ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-warm-gray">Selecting for:</span>
                    <span className={cn(
                      'px-2 py-1 rounded text-xs font-bold',
                      SLOT_CONFIG[activeSlot]?.bgColor || 'bg-zinc-500/20',
                      SLOT_CONFIG[activeSlot]?.color || 'text-warm-gray'
                    )}>
                      {SLOT_CONFIG[activeSlot]?.label || activeSlot}
                    </span>
                    <span className="text-xs text-tertiary">
                      ({allowedPositionsForActiveSlot.join('/')})
                    </span>
                  </div>
                ) : (
                  <span className="text-sm text-warm-gray">Click a roster slot to select a card</span>
                )}
              </div>
            </div>

            {/* Search */}
            <div className="px-4 py-2 border-b border-surface-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary" />
                <input
                  type="text"
                  placeholder="Search players..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-surface-3 border border-surface-4 rounded-lg text-cream text-sm focus:outline-none focus:border-zinc-600"
                />
              </div>
            </div>

            {/* Card list */}
            <div className="flex-1 overflow-y-auto px-4 py-2 min-h-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
                </div>
              ) : cards.length === 0 ? (
                /* Empty deck — the #1 "button doesn't work" confusion: the modal
                   opened but there was nothing to build with. Say so, and route
                   to packs. */
                <div className="flex flex-col items-center justify-center py-12 text-center px-6">
                  <AlertCircle className="w-8 h-8 mb-3 text-gold" />
                  <p className="text-sm font-bold text-cream mb-1">
                    You don&apos;t own any {isBestBall ? 'NFL ' : ''}cards yet
                  </p>
                  <p className="text-xs text-warm-gray mb-4 max-w-[36ch]">
                    Leagues are entered by locking cards from your collection.
                    Pull packs from The Mint to build your deck
                    {isBestBall ? ' — two packs field a full roster' : ''}.
                  </p>
                  <a
                    href="/store?tab=packs"
                    className="px-4 py-2 rounded-lg bg-gold text-ink text-sm font-bold hover:bg-gold/90 transition-colors"
                  >
                    Get Packs
                  </a>
                </div>
              ) : isBestBall && cards.length < rosterSize ? (
                <div className="flex flex-col items-center justify-center py-12 text-center px-6">
                  <AlertCircle className="w-8 h-8 mb-3 text-gold" />
                  <p className="text-sm font-bold text-cream mb-1">
                    {cards.length} of {rosterSize} cards needed
                  </p>
                  <p className="text-xs text-warm-gray mb-4 max-w-[36ch]">
                    You need {rosterSize - cards.length} more NFL card
                    {rosterSize - cards.length === 1 ? '' : 's'} to field a roster.
                  </p>
                  <a
                    href="/store?tab=packs"
                    className="px-4 py-2 rounded-lg bg-gold text-ink text-sm font-bold hover:bg-gold/90 transition-colors"
                  >
                    Get More Packs
                  </a>
                </div>
              ) : !activeSlot ? (
                <div className="flex flex-col items-center justify-center py-12 text-tertiary">
                  <AlertCircle className="w-8 h-8 mb-2" />
                  <p className="text-sm">Select a roster slot on the left to choose a card</p>
                </div>
              ) : filteredCards.length === 0 && blockedCardsForSlot.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-tertiary">
                  <AlertCircle className="w-8 h-8 mb-2" />
                  <p className="text-sm">No eligible {allowedPositionsForActiveSlot.join('/')} cards found</p>
                  {!isBestBall && rarityGate !== 'OPEN' && (
                    <p className="text-xs mt-1">
                      You need {allowedRarities.map((r) => RARITY_CONFIG[r].label).join(' or ')} cards
                    </p>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {filteredCards.map((card) => (
                    <motion.div
                      key={card.id}
                      layout
                      whileHover={{ scale: 1.02 }}
                      className={cn(
                        'relative rounded-lg border-2 transition-colors cursor-pointer overflow-hidden',
                        'border-surface-4 hover:border-gold'
                      )}
                      onClick={() => assignCardToSlot(card)}
                    >
                      <PlayerCardCompact card={card} />
                    </motion.div>
                  ))}
                  {/* Same-window locked cards — visible but disabled with reason */}
                  {blockedCardsForSlot.map((card) => (
                    <div
                      key={card.id}
                      className="relative rounded-lg border-2 border-surface-4 opacity-60 cursor-not-allowed overflow-hidden"
                      title={blockedReasons.get(card.id)}
                    >
                      <PlayerCardCompact card={card} />
                      <span className="absolute top-1 right-1 flex items-center gap-1 px-1.5 py-0.5 rounded bg-surface/90 text-[9px] text-warm-gray border border-surface-4 max-w-[85%]">
                        <Lock className="w-2.5 h-2.5 shrink-0" />
                        <span className="truncate">{blockedReasons.get(card.id)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="px-4 py-2 border-t border-surface-3 text-xs text-tertiary">
              {eligibleCards.length} eligible cards | {ineligibleCards.length} ineligible
            </div>
          </div>
        </div>

        {/* Structured entry failure (GATE_NOT_MET shortfalls, shape, locks…) */}
        {entryError && (
          <div className="mx-4 mt-2 p-3 rounded-lg bg-oxblood/10 border border-oxblood/30 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-oxblood mt-0.5 shrink-0" />
            <span className="text-sm text-oxblood">{entryError}</span>
          </div>
        )}

        <ModalFooter className="gap-2">
          {isBestBall && filledSlots === effectiveSlots.length && !gatesSatisfied && (
            <span className="text-xs text-warm-gray mr-auto">
              League gates not met yet — swap in higher-class cards.
            </span>
          )}
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={() => setConfirmingLock(true)}
            disabled={!isValid || submitting || !canAfford}
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-cream/30 border-t-cream rounded-full animate-spin mr-2" />
                Entering...
              </>
            ) : (
              `${isBestBall ? 'Enter League' : 'Enter Event'} (${filledSlots}/${effectiveSlots.length} slots)`
            )}
          </Button>
        </ModalFooter>

        {/* Lock confirmation — the last stop before cards are committed. */}
        {confirmingLock && (
          <div
            className="absolute inset-0 z-20 flex items-center justify-center rounded-2xl bg-surface/75 backdrop-blur-sm p-4"
            onClick={() => !submitting && setConfirmingLock(false)}
            onKeyDown={(e) => {
              // Scope Escape to the confirmation only — without this, Radix
              // closes the whole roster modal underneath.
              if (e.key === 'Escape') {
                e.stopPropagation();
                setConfirmingLock(false);
              }
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="lock-confirm-title"
              className="w-full max-w-sm rounded-xl border border-gold/40 bg-surface-2 p-5 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-2 flex items-center gap-2">
                <Lock className="h-4 w-4 text-gold" />
                <h3 id="lock-confirm-title" className="font-serif text-lg font-bold text-cream">
                  Lock cards to this {isBestBall ? 'league' : 'event'}?
                </h3>
              </div>
              <p className="mb-4 text-sm leading-snug text-warm-gray">
                Your {filledSlots} selected cards will be locked in. {lockScopeCopy}
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setConfirmingLock(false)} disabled={submitting}>
                  Go back
                </Button>
                <Button
                  autoFocus
                  onClick={() => {
                    setConfirmingLock(false);
                    void handleConfirm();
                  }}
                  disabled={submitting}
                >
                  <Lock className="mr-1.5 h-3.5 w-3.5" />
                  Lock & Enter
                </Button>
              </div>
            </div>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
}

export default RosterSelectionModal;
